import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Menu, MenuItem, Dialog, TextField, Button, CircularProgress } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CloseIcon from '@mui/icons-material/Close';
import { ordersService } from '../../services/orders.service';
import { AdminOrderListItem, OrderStatus } from '../../types/orders.types';

const OrderDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [hasRefund, setHasRefund] = useState(false);

  const [orderDetails, setOrderDetails] = useState<AdminOrderListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!id) {
        setError('Order ID is missing');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await ordersService.getOrderDetails(id);
        setOrderDetails(response.data);
      } catch (err) {
        console.error('Failed to fetch order details:', err);
        setError(err instanceof Error ? err.message : 'Failed to load order details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !orderDetails) {
    return (
      <Box sx={{ padding: { xs: '24px 16px', sm: '40px 40px', md: '40px 80px' } }}>
        <Typography color="error" sx={{ fontSize: 'var(--palette-light-font-size-h3-value)' }}>
          {error || 'Order not found'}
        </Typography>
        <Button onClick={() => navigate('/orders')} sx={{ marginTop: 'var(--palette-light-4-value)' }}>
          Back to Orders
        </Button>
      </Box>
    );
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = async (newStatus: OrderStatus) => {
    if (!orderDetails) return;

    try {
      setLoading(true);
      await ordersService.updateOrderStatus(orderDetails.orderId, { status: newStatus });
      const response = await ordersService.getOrderDetails(orderDetails.orderId);
      setOrderDetails(response.data);
      handleMenuClose();
    } catch (err) {
      console.error('Failed to update order status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update order status');
    } finally {
      setLoading(false);
    }
  };

  const handleReplyDialogOpen = () => {
    setReplyDialogOpen(true);
  };

  const handleReplyDialogClose = () => {
    setReplyDialogOpen(false);
    setReplyMessage('');
  };

  const handleSendReply = () => {
    console.log('Sending reply:', replyMessage);
    handleReplyDialogClose();
  };

  const handleRefund = () => {
    setHasRefund(true);
    handleMenuClose();
  };

  const handleConfirmRefund = () => {
    console.log('Refund confirmed');
    setHasRefund(false);
  };

  const handleDeclineRefund = () => {
    console.log('Refund declined');
    setHasRefund(false);
  };

  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.APPROVED:
        return 'var(--palette-light-status-success-value-hex)';
      case OrderStatus.PENDING:
      case OrderStatus.DRAFT:
        return 'var(--palette-light-text-tertiary-value-hex)';
      case OrderStatus.CHANGING:
        return '#FFA500';
      case OrderStatus.CANCELLED:
      case OrderStatus.DECLINED:
        return 'var(--palette-light-status-danger-value-hex)';
      case OrderStatus.COMPLETED:
        return 'var(--palette-light-background-tertiary-value-hex)';
      case OrderStatus.IN_PROGRESS:
      case OrderStatus.DELIVERY:
        return 'var(--palette-light-brand-secondary-value-hex)';
      case OrderStatus.REFUNDED:
        return 'var(--palette-light-brand-secondary-value-hex)';
      default:
        return 'var(--palette-light-text-primary-value-hex)';
    }
  };

  const formatDate = (timestamp: number | string | undefined) => {
    if (!timestamp) return 'N/A';
    const date = new Date(typeof timestamp === 'string' ? timestamp : timestamp);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined || amount === null) return 'QAR 0.00';
    return `QAR ${amount.toFixed(2)}`;
  };

  const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <Typography sx={{ fontSize: 'var(--palette-light-font-size-h4-value)', fontWeight: 700, color: 'var(--palette-light-text-primary-value-hex)' }}>
      {children}
    </Typography>
  );

  const PropertyRow = ({ label, value, noBorder }: { label: string; value: string | undefined; noBorder?: boolean }) => (
    <Box sx={{
      display: 'flex',
      gap: 'var(--palette-light-1-value)',
      padding: 'var(--palette-light-1-value) 0',
      borderBottom: noBorder ? 'none' : '1px solid #F1F4F4',
      alignItems: 'flex-start',
    }}>
      <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-secondary-value-hex)', flex: 1 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)', flex: 1, whiteSpace: 'pre-line' }}>
        {value || 'N/A'}
      </Typography>
    </Box>
  );

  const SectionBox = ({ children }: { children: React.ReactNode }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)' }}>
      {children}
    </Box>
  );

  return (
    <Box sx={{
      padding: { xs: '24px 16px', sm: '40px 40px', md: '40px 80px' },
      gap: 'var(--palette-light-6-value)',
      width: '100%',
      minHeight: '100%',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--palette-light-4-value)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--palette-light-3-value)' }}>
          <IconButton
            onClick={() => navigate('/orders')}
            sx={{
              width: 'var(--palette-light-8-value)',
              height: 'var(--palette-light-8-value)',
              color: 'var(--palette-light-brand-primary-value-hex)',
              padding: 0,
              '&:hover': { backgroundColor: 'rgba(103, 49, 79, 0.04)' },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: '16px' }} />
          </IconButton>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-h1-value)', fontWeight: 700, lineHeight: 1.15, color: 'var(--palette-light-text-primary-value-hex)' }}>
            Order #{orderDetails.orderNumber}
          </Typography>
          <Box sx={{
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            padding: '0 var(--palette-light-3-value)', height: 'var(--palette-light-8-value)',
            backgroundColor: getStatusColor(orderDetails.status), borderRadius: 'var(--palette-light-2-value)',
          }}>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-foreground-value-hex)' }}>
              {orderDetails.status}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={handleMenuOpen}
          sx={{
            width: '56px', height: '56px',
            border: '1px solid var(--palette-light-brand-primary-value-hex)',
            borderRadius: 'var(--palette-light-4-value)',
            color: 'var(--palette-light-brand-primary-value-hex)',
            '&:hover': { backgroundColor: 'rgba(103, 49, 79, 0.04)' },
          }}
        >
          <MoreHorizIcon />
        </IconButton>
      </Box>

      {/* Main Content */}
      <Box sx={{ display: 'flex', gap: 'var(--palette-light-10-value)', flexWrap: 'wrap' }}>
        {/* Left Column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-8-value)', flex: 1, minWidth: '300px' }}>

          {/* Vendor Section */}
          <SectionBox>
            <SectionTitle>Vendor</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <PropertyRow label="Name" value={orderDetails.vendor.name} />
              <PropertyRow label="Category" value={orderDetails.vendor.category} />
              <PropertyRow label="Vendor ID" value={orderDetails.vendor.vendorId} noBorder />
            </Box>
          </SectionBox>

          {/* Customer Section */}
          <SectionBox>
            <SectionTitle>Customer</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <PropertyRow label="Name" value={orderDetails.customer.name || orderDetails.contactDetails?.contactName} />
              <PropertyRow label="Phone" value={orderDetails.customer.phone || orderDetails.contactDetails?.phoneNumber} />
              <PropertyRow label="Email" value={orderDetails.contactDetails?.email} />
              <PropertyRow label="User ID" value={orderDetails.customer.userId || orderDetails.userId} noBorder />
            </Box>
          </SectionBox>

          {/* Event Section */}
          <SectionBox>
            <SectionTitle>Event</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <PropertyRow label="Event Name" value={orderDetails.eventDetails?.eventName} />
              <PropertyRow label="Date & Time" value={orderDetails.event?.dateTime || orderDetails.eventDetails?.eventDate || orderDetails.startDateTime} />
              <PropertyRow label="Address" value={orderDetails.event?.address || orderDetails.eventDetails?.eventAddress} />
              <PropertyRow label="Zone" value={orderDetails.eventDetails?.eventZone} />
              <PropertyRow label="Building Number" value={orderDetails.eventDetails?.eventBuildingNumber} />
              <PropertyRow label="Street Number" value={orderDetails.eventDetails?.eventStreetNumber} />
              <PropertyRow label="Event ID" value={orderDetails.event?.eventId || orderDetails.eventId} noBorder />
            </Box>
          </SectionBox>

          {/* Special Requests */}
          {orderDetails.specialRequests && (
            <SectionBox>
              <SectionTitle>Special Requests</SectionTitle>
              <Box sx={{
                padding: 'var(--palette-light-3-value)',
                backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                borderRadius: 'var(--palette-light-4-value)',
              }}>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)', whiteSpace: 'pre-line' }}>
                  {orderDetails.specialRequests}
                </Typography>
              </Box>
            </SectionBox>
          )}

          {/* Dispute Section */}
          {orderDetails.hasDispute && (
            <SectionBox>
              <Box sx={{ display: 'flex', gap: 'var(--palette-light-4-value)', alignItems: 'center' }}>
                <Box component="img" src="/icons/dispute-icon.svg" alt="Dispute" sx={{ width: '24px', height: '24px' }} />
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-h4-value)', fontWeight: 700, color: '#925B64' }}>
                  Dispute
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <PropertyRow label="Reason" value={orderDetails.disputeReason} />
                <PropertyRow label="Date" value={orderDetails.disputeDate ? formatDate(orderDetails.disputeDate) : undefined} noBorder />
              </Box>
              <Box
                component="button"
                onClick={handleReplyDialogOpen}
                sx={{
                  alignSelf: 'stretch', height: '56px',
                  padding: '2px var(--palette-light-8-value) 0',
                  borderRadius: 'var(--palette-light-4-value)',
                  border: '1px solid var(--palette-light-brand-primary-value-hex)',
                  backgroundColor: 'transparent',
                  color: 'var(--palette-light-brand-primary-value-hex)',
                  fontSize: 'var(--palette-light-font-size-default-value)',
                  fontWeight: 700, lineHeight: 1.5, letterSpacing: '-0.02em',
                  cursor: 'pointer',
                  fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                  '&:hover': { backgroundColor: 'rgba(103, 49, 79, 0.04)' },
                }}
              >
                Reply
              </Box>
            </SectionBox>
          )}

          {/* Refund Section */}
          {hasRefund && (
            <SectionBox>
              <Box sx={{ display: 'flex', gap: 'var(--palette-light-4-value)', alignItems: 'center' }}>
                <Box component="img" src="/icons/money-change.svg" alt="Refund" sx={{ width: '24px', height: '24px' }} />
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-h4-value)', fontWeight: 700, color: 'var(--palette-light-brand-secondary-value-hex)' }}>
                  Refund
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                Vendor initiated a refund
              </Typography>
              <Box sx={{ display: 'flex', gap: 'var(--palette-light-2-value)', padding: 'var(--palette-light-1-value) 0', borderBottom: '1px solid #F1F4F4' }}>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)', flex: 1 }}>Amount</Typography>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)', flex: 1 }}>{formatCurrency((orderDetails.amount || 0) - (orderDetails.discountAmount || 0) + (orderDetails.shippingFee || 0))}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 'var(--palette-light-4-value)' }}>
                <Button onClick={handleConfirmRefund} variant="contained" fullWidth sx={{
                  height: '56px', borderRadius: 'var(--palette-light-4-value)',
                  backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
                  color: 'var(--palette-light-text-foreground-value-hex)',
                  fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 700,
                  textTransform: 'none', '&:hover': { opacity: 0.9 },
                }}>Confirm</Button>
                <Button onClick={handleDeclineRefund} variant="outlined" fullWidth sx={{
                  height: '56px', borderRadius: 'var(--palette-light-4-value)',
                  border: '1px solid var(--palette-light-brand-primary-value-hex)',
                  color: 'var(--palette-light-brand-primary-value-hex)',
                  fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 700,
                  textTransform: 'none', '&:hover': { backgroundColor: 'rgba(103, 49, 79, 0.04)' },
                }}>Decline</Button>
              </Box>
            </SectionBox>
          )}

          {/* Order Timestamps */}
          <SectionBox>
            <SectionTitle>Order Info</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <PropertyRow label="Order ID" value={orderDetails.orderId} />
              <PropertyRow label="Created" value={formatDate(orderDetails.createdAt)} />
              <PropertyRow label="Updated" value={formatDate(orderDetails.updatedAt)} />
              <PropertyRow label="Cart ID" value={orderDetails.cartId} />
              <PropertyRow label="Product ID" value={orderDetails.productId} noBorder />
            </Box>
          </SectionBox>
        </Box>

        {/* Right Column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-8-value)', flex: 1, minWidth: '300px' }}>

          {/* Products & Packages */}
          <SectionBox>
            <SectionTitle>Products</SectionTitle>
            <Box sx={{
              display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-2-value)',
              padding: 'var(--palette-light-4-value)',
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
              borderRadius: 'var(--palette-light-4-value)',
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 700, color: 'var(--palette-light-text-primary-value-hex)' }}>
                  {orderDetails.product.name}
                </Typography>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                  {formatCurrency(orderDetails.amount || orderDetails.total)}
                </Typography>
              </Box>
              {orderDetails.product.details && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-1-value)' }}>
                  {orderDetails.product.details.split(',').map((item, index) => (
                    <Typography key={index} sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                      {item.trim()}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>

            {/* Package Breakdown */}
            {orderDetails.packages && orderDetails.packages.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-2-value)' }}>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', fontWeight: 700, color: 'var(--palette-light-text-secondary-value-hex)' }}>
                  Package Details
                </Typography>
                {orderDetails.packages.map((pkg, index) => (
                  <Box key={index} sx={{
                    padding: 'var(--palette-light-3-value)',
                    backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                    borderRadius: 'var(--palette-light-2-value)',
                  }}>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 700, color: 'var(--palette-light-text-primary-value-hex)' }}>
                      {pkg.title}{pkg.subTitle ? ` - ${pkg.subTitle}` : ''}
                    </Typography>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)', textTransform: 'capitalize' }}>
                      {pkg.type}
                    </Typography>
                    {pkg.selectedOptions && pkg.selectedOptions.length > 0 && (
                      <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-primary-value-hex)', marginTop: '4px' }}>
                        Selected: {pkg.selectedOptions.join(', ')}
                      </Typography>
                    )}
                    {pkg.selectedItems && pkg.selectedItems.length > 0 && (
                      <Box sx={{ marginTop: '4px' }}>
                        {pkg.selectedItems.map((item, i) => (
                          <Typography key={i} sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                            {item.itemId} x{item.units}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </SectionBox>

          {/* Pricing Breakdown */}
          <SectionBox>
            <SectionTitle>Pricing</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <PropertyRow label="Subtotal" value={formatCurrency(orderDetails.amount)} />
              {orderDetails.discountAmount !== undefined && orderDetails.discountAmount > 0 && (
                <PropertyRow
                  label={`Discount${orderDetails.discountType ? ` (${orderDetails.discountType})` : ''}`}
                  value={`-${formatCurrency(orderDetails.discountAmount)}`}
                />
              )}
              {orderDetails.shippingFee !== undefined && orderDetails.shippingFee > 0 && (
                <PropertyRow label="Shipping Fee" value={formatCurrency(orderDetails.shippingFee)} />
              )}
              <Box sx={{
                display: 'flex', gap: 'var(--palette-light-1-value)',
                padding: 'var(--palette-light-2-value) 0',
                borderTop: '2px solid #F1F4F4', marginTop: 'var(--palette-light-1-value)',
              }}>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 700, color: 'var(--palette-light-text-primary-value-hex)', flex: 1 }}>
                  Total
                </Typography>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 700, color: 'var(--palette-light-text-primary-value-hex)', flex: 1 }}>
                  {formatCurrency((orderDetails.amount || 0) - (orderDetails.discountAmount || 0) + (orderDetails.shippingFee || 0))}
                </Typography>
              </Box>
            </Box>
          </SectionBox>
        </Box>
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 'var(--palette-light-2-value)',
            boxShadow: '0px 40px 60px 0px rgba(26, 109, 109, 0.1)',
            minWidth: '240px', padding: 'var(--palette-light-2-value) 0 0',
          },
          '& .MuiList-root': { padding: 0 },
        }}
      >
        <Box sx={{ padding: 'var(--palette-light-4-value) var(--palette-light-6-value) var(--palette-light-2-value)', borderBottom: 'none' }}>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
            Change Status
          </Typography>
        </Box>
        {[OrderStatus.PENDING, OrderStatus.APPROVED, OrderStatus.IN_PROGRESS, OrderStatus.DELIVERY, OrderStatus.COMPLETED].map((status) => (
          <MenuItem key={status} onClick={() => handleStatusChange(status)} sx={{
            fontSize: 'var(--palette-light-font-size-default-value)',
            color: 'var(--palette-light-text-primary-value-hex)',
            padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
            '&:hover': { backgroundColor: 'var(--palette-light-background-secondary-value-hex)' },
          }}>
            {status}
          </MenuItem>
        ))}
        <Box sx={{ padding: 'var(--palette-light-4-value) var(--palette-light-6-value) var(--palette-light-2-value)', borderBottom: 'none', marginTop: 'var(--palette-light-2-value)' }}>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
            Refund or cancel
          </Typography>
        </Box>
        <MenuItem onClick={handleRefund} sx={{
          fontSize: 'var(--palette-light-font-size-default-value)',
          color: 'var(--palette-light-text-primary-value-hex)',
          padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
          '&:hover': { backgroundColor: 'var(--palette-light-background-secondary-value-hex)' },
        }}>
          Refund
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange(OrderStatus.CANCELLED)} sx={{
          fontSize: 'var(--palette-light-font-size-default-value)',
          color: 'var(--palette-light-status-danger-value-hex)',
          padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
          marginBottom: 'var(--palette-light-2-value)',
          '&:hover': { backgroundColor: 'var(--palette-light-background-secondary-value-hex)' },
        }}>
          Cancel order
        </MenuItem>
      </Menu>

      {/* Reply to Dispute Dialog */}
      <Dialog
        open={replyDialogOpen}
        onClose={handleReplyDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 'var(--palette-light-3-value)', boxShadow: '0px 80px 250px 0px rgba(26, 109, 109, 0.1)', width: '508px', maxWidth: '508px' } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--palette-light-6-value)', position: 'relative' }}>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-h3-value)', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.005em', color: 'var(--palette-light-text-primary-value-hex)' }}>
            Reply to dispute
          </Typography>
          <IconButton onClick={handleReplyDialogClose} sx={{
            position: 'absolute', right: 'var(--palette-light-6-value)', top: 'var(--palette-light-6-value)',
            backgroundColor: 'var(--palette-light-background-secondary-value-hex)', borderRadius: '40px', padding: 'var(--palette-light-2-value)',
            '&:hover': { backgroundColor: 'var(--palette-light-background-tertiary-value-hex)' },
          }}>
            <CloseIcon sx={{ fontSize: '24px' }} />
          </IconButton>
        </Box>
        <Box sx={{ padding: '0 var(--palette-light-6-value) var(--palette-light-6-value)', display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-8-value)' }}>
          <TextField
            label="Message" multiline rows={6} value={replyMessage}
            onChange={(e) => setReplyMessage(e.target.value)} placeholder="Placeholder" fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                borderRadius: 'var(--palette-light-4-value)',
                padding: 'var(--palette-light-1-5-value) var(--palette-light-3-value)',
                '& fieldset': { border: 'none' }, '&:hover fieldset': { border: 'none' }, '&.Mui-focused fieldset': { border: 'none' },
              },
              '& .MuiInputLabel-root': {
                fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-primary-value-hex)',
                transform: 'translate(12px, 8px) scale(1)',
                '&.MuiInputLabel-shrink': { transform: 'translate(12px, 8px) scale(1)' },
              },
              '& .MuiInputBase-input': {
                fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)',
                padding: '4px 0 0 0', marginTop: 'var(--palette-light-4-value)',
              },
            }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)' }}>
            <Button onClick={handleSendReply} variant="contained" fullWidth sx={{
              height: '56px', borderRadius: 'var(--palette-light-4-value)',
              backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
              color: 'var(--palette-light-text-foreground-value-hex)',
              fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 700,
              textTransform: 'none', '&:hover': { opacity: 0.9 },
            }}>Send</Button>
            <Button onClick={handleReplyDialogClose} variant="text" fullWidth sx={{
              height: '56px', borderRadius: 'var(--palette-light-4-value)',
              color: 'var(--palette-light-brand-primary-value-hex)',
              fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 700,
              textTransform: 'none', '&:hover': { backgroundColor: 'rgba(103, 49, 79, 0.04)' },
            }}>Cancel</Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default OrderDetail;
